import { HowToAnswer } from "./interactions/HowToAnswer";
import { useEffect } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface Choice<V extends string> {
  value: V;
  label: string;
  hint?: string;
}

interface Props<V extends string> {
  title: string;
  subtitle?: string;
  choices: readonly Choice<V>[];
  value?: V;
  onSelect: (v: V) => void;
  /** Optional acknowledgement line shown briefly after selection. */
  ackFor?: Partial<Record<V, string>>;
  /**
   * When supplied, the screen does NOT advance on selection. The learner
   * picks, sees the pick land, and then presses Continue. Nobody should
   * have to guess whether a tap registered.
   */
  onContinue?: () => void;
  continueLabel?: string;
}

/**
 * Single-choice screen. Keyboard: 1..9 to pick, Enter to continue.
 *
 * Two modes:
 *   · with `onContinue` — selection is sticky and an explicit Continue
 *     button commits it (the default everywhere in the walk);
 *   · without it — the caller schedules its own ~250ms auto-advance.
 */
export function ChoiceScreen<V extends string>({
  title, subtitle, choices, value, onSelect, ackFor, onContinue,
  continueLabel = "Continue",
}: Props<V>) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Enter" && onContinue && value !== undefined) {
        onContinue();
        return;
      }
      const n = Number(e.key);
      if (!Number.isFinite(n) || n < 1 || n > choices.length) return;
      const c = choices[n - 1];
      if (c) onSelect(c.value);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [choices, onSelect, onContinue, value]);

  const ack = value && ackFor?.[value];
  const reduce = useReducedMotion();

  return (
    <div className="w-full max-w-2xl mx-auto flex flex-col">
      <h1 className="font-display text-3xl md:text-4xl font-medium leading-tight text-foreground">
        {title}
      </h1>
      {subtitle && (
        <p className="mt-3 text-base text-muted-foreground">{subtitle}</p>
      )}
      <div className="mt-4"><HowToAnswer>Tap the one answer that fits you best, then tap Continue.</HowToAnswer></div>

      <div className="mt-8 flex flex-col gap-3">
        {choices.map((c, i) => {
          const selected = c.value === value;
          return (
            <motion.button
              key={c.value}
              onClick={() => onSelect(c.value)}
              aria-pressed={selected}
              whileTap={reduce ? undefined : { scale: 0.985 }}
              transition={{ type: "spring", stiffness: 420, damping: 30 }}
              className={cn(
                "group relative overflow-hidden text-left rounded-3xl border px-5 py-4 min-h-[64px]",
                "transition-colors duration-200 ease-out",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                selected
                  ? "border-primary bg-primary/10 shadow-lg shadow-primary/10"
                  : "glass-card border-border hover:border-primary/40 hover:bg-card/80",
              )}
            >
              <div className="flex items-start gap-4">
                <span
                  className={cn(
                    "shrink-0 mt-0.5 h-7 w-7 rounded-full grid place-items-center text-xs font-medium",
                    "transition-colors duration-200",
                    selected
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-muted-foreground group-hover:text-foreground",
                  )}
                  aria-hidden
                >
                  {selected ? <Check className="h-4 w-4" /> : i + 1}
                </span>
                <span className="flex-1">
                  <span className="block text-base md:text-lg text-foreground">{c.label}</span>
                  {c.hint && (
                    <span className="block mt-1 text-sm text-muted-foreground">{c.hint}</span>
                  )}
                </span>
              </div>
            </motion.button>
          );
        })}
      </div>


      <AnimatePresence>
        {ack && (
          <motion.p
            key={ack}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="mt-6 text-sm text-muted-foreground italic"
          >
            {ack}
          </motion.p>
        )}
      </AnimatePresence>

      {onContinue && (
        <div className="mt-10 flex justify-end">
          <button
            onClick={onContinue}
            disabled={value === undefined}
            className="min-h-[48px] px-6 rounded-2xl bg-primary text-primary-foreground font-medium
              inline-flex items-center gap-2
              disabled:opacity-40 disabled:pointer-events-none
              focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            {continueLabel} <ArrowRight className="h-4 w-4" aria-hidden />
          </button>
        </div>
      )}
    </div>

  );
}
